import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { getDateLocale } from '../utils/dateLocale';

interface Session {
  session_id: string;
  session_title: string;
  last_message_at: string;
  last_message: string;
}

interface SearchResult {
  session_id: string;
  session_title: string;
  snippet: string;
  last_message_at: string;
}

export default function SessionSidebar() {
  const { t } = useTranslation('sidebar');
  const { idToken, impersonatedStudentId, preferredLanguage } = useAuth();
  const navigate = useNavigate();
  const { sessionId: currentSessionId } = useParams();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const apiUrl =
    window.__PAILA_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || '/api';

  useEffect(() => {
    const fetchSessions = async () => {
      if (!idToken) return;

      try {
        const fetchHeaders: Record<string, string> = {
          Authorization: `Bearer ${idToken}`,
        };
        if (impersonatedStudentId) {
          fetchHeaders['X-Impersonate-Student-Id'] = impersonatedStudentId;
        }
        const langParam = preferredLanguage && preferredLanguage !== 'en' ? `?language=${preferredLanguage}` : '';
        const response = await fetch(`${apiUrl}/sessions${langParam}`, {
          headers: fetchHeaders,
        });

        if (response.ok) {
          const data = await response.json();
          setSessions(data.sessions || []);
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [idToken, apiUrl, impersonatedStudentId, preferredLanguage]);

  const searchSessions = useCallback(
    async (query: string) => {
      if (!idToken || query.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }

      setSearching(true);
      try {
        const searchHeaders: Record<string, string> = {
          Authorization: `Bearer ${idToken}`,
        };
        if (impersonatedStudentId) {
          searchHeaders['X-Impersonate-Student-Id'] = impersonatedStudentId;
        }
        const langParam = preferredLanguage && preferredLanguage !== 'en' ? `&language=${preferredLanguage}` : '';
        const response = await fetch(
          `${apiUrl}/sessions/search?q=${encodeURIComponent(query)}${langParam}`,
          {
            headers: searchHeaders,
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.results || []);
        }
      } catch (error) {
        console.error('Error searching sessions:', error);
      } finally {
        setSearching(false);
      }
    },
    [idToken, apiUrl, impersonatedStudentId, preferredLanguage]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchSessions(value);
    }, 300);
  };

  const getDisplayDate = (session: Session) => {
    return session.last_message_at || session.last_message || '';
  };

  const getDisplayTitle = (session: Session) => {
    if (session.session_title) {
      return session.session_title;
    }
    // Fallback for sessions without metadata
    const dateStr = getDisplayDate(session);
    return dateStr ? formatSessionDate(dateStr) : t('untitledConversation');
  };

  const dateLocale = getDateLocale(preferredLanguage);

  const formatSessionDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const opts = { locale: dateLocale };
    if (isToday(date)) {
      return format(date, t('todayAt'), opts);
    } else if (isYesterday(date)) {
      return format(date, t('yesterdayAt'), opts);
    } else if (isThisWeek(date)) {
      return format(date, "EEEE 'at' h:mm a", opts);
    } else {
      return format(date, "d MMM 'at' h:mm a", opts);
    }
  };

  const isSearching = searchQuery.length >= 2;

  if (loading) {
    return (
      <div className="p-4" role="status" aria-label={t('loadingConversations')}>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"
            />
          ))}
        </div>
      </div>
    );
  }

  // Group sessions by date category
  const categoryKeys = ['today', 'yesterday', 'thisWeek', 'older'] as const;
  const grouped: { [key: string]: Session[] } = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: [],
  };

  sessions.forEach((session) => {
    const dateStr = getDisplayDate(session);
    if (!dateStr) {
      grouped['older'].push(session);
      return;
    }
    const date = new Date(dateStr);
    if (isToday(date)) {
      grouped['today'].push(session);
    } else if (isYesterday(date)) {
      grouped['yesterday'].push(session);
    } else if (isThisWeek(date)) {
      grouped['thisWeek'].push(session);
    } else {
      grouped['older'].push(session);
    }
  });

  return (
    <div className="p-2">
      {/* Search bar */}
      <div className="px-2 pb-3">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            aria-label={t('searchAriaLabel')}
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-uel-purple-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              aria-label={t('clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Search results */}
      {isSearching && (
        <div>
          {searching ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400" role="status" aria-live="polite">
              {t('searching')}
            </div>
          ) : searchResults.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400" role="status" aria-live="polite">
              {t('noResults')}
            </div>
          ) : (
            <div className="space-y-1">
              {searchResults.map((result) => (
                <button
                  key={result.session_id}
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    navigate(`/chat/${result.session_id}`);
                  }}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg text-sm
                    transition-colors duration-150
                    ${
                      currentSessionId === result.session_id
                        ? 'bg-uel-purple-100 dark:bg-uel-purple-900/30 text-uel-purple-700 dark:text-uel-purple-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }
                  `}
                >
                  <div className="font-medium truncate">
                    {result.session_title || t('untitledConversation')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                    {result.snippet}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Normal session list */}
      {!isSearching && (
        <>
          {sessions.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
              {t('noConversations')}
            </div>
          ) : (
            categoryKeys.map(
              (categoryKey) =>
                grouped[categoryKey].length > 0 && (
                  <div key={categoryKey} className="mb-4">
                    <h3 className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      {t(categoryKey)}
                    </h3>
                    <div className="space-y-1">
                      {grouped[categoryKey].map((session) => (
                        <button
                          key={session.session_id}
                          onClick={() => navigate(`/chat/${session.session_id}`)}
                          aria-current={currentSessionId === session.session_id ? 'page' : undefined}
                          className={`
                            w-full text-left px-3 py-2 rounded-lg text-sm
                            transition-colors duration-150
                            ${
                              currentSessionId === session.session_id
                                ? 'bg-uel-purple-100 dark:bg-uel-purple-900/30 text-uel-purple-700 dark:text-uel-purple-300'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }
                          `}
                        >
                          <div className="flex items-start">
                            <svg
                              className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">
                                {getDisplayTitle(session)}
                              </div>
                              {session.session_title && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {formatSessionDate(getDisplayDate(session))}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )
            )
          )}
        </>
      )}
    </div>
  );
}
